import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import jsQR from 'jsqr'
import {
  AlertTriangle,
  Clock,
  Download,
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  QrCode,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { SettingsLayout } from '../components/settings-layout'
import { MobileBottomSlots } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus } from '../components/ui/mobile-action-bar'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import { api, type FetcherAccount, type FetcherProvider as FetcherProviderType } from '../lib/api'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/fetcher',
  component: FetcherSettings,
})

// ============================================================================
// QR Code TOTP Extraction
// ============================================================================

async function extractTotpFromQrImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Canvas-Context konnte nicht erstellt werden'))
        return
      }
      ctx.drawImage(img, 0, 0)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)

      const code = jsQR(imageData.data, imageData.width, imageData.height)
      if (!code) {
        reject(new Error('QR-Code konnte nicht gelesen werden'))
        return
      }

      // Parse otpauth://totp/Label?secret=XXXX&issuer=...
      const url = code.data
      if (!url.startsWith('otpauth://')) {
        reject(new Error('Kein gültiger TOTP QR-Code'))
        return
      }

      const params = new URLSearchParams(url.split('?')[1] || '')
      const secret = params.get('secret')
      if (!secret) {
        reject(new Error('Kein Secret im QR-Code gefunden'))
        return
      }

      resolve(secret)
    }
    img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'))
    img.src = URL.createObjectURL(file)
  })
}

function FetcherSettings() {
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const qc = useQueryClient()

  const { data: accountsData } = useQuery({
    queryKey: ['fetcher', 'accounts'],
    queryFn: () => api.listFetcherAccounts(),
    enabled: meData?.user?.role === 'admin',
    refetchInterval: 5000, // Poll to catch status changes during sync
  })

  const { data: providersData } = useQuery({
    queryKey: ['fetcher', 'providers'],
    queryFn: () => api.listFetcherProviders(),
    enabled: meData?.user?.role === 'admin',
  })

  const [addOpen, setAddOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<FetcherAccount | null>(null)
  const [twoFaTarget, setTwoFaTarget] = useState<string | null>(null)

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteFetcherAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fetcher'] })
      notify.success('Konto gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const syncMutation = useMutation({
    mutationFn: (id: string) => api.syncFetcherAccount(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['fetcher'] })
      if (data.alreadySyncing) {
        notify.warning('Synchronisation läuft bereits')
      } else {
        notify.success('Synchronisation gestartet')
      }
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const accounts = accountsData?.accounts ?? []
  const providers = providersData?.providers ?? []

  // Check if any account is awaiting 2FA
  const awaitingAccount = accounts.find((a) => a.status === 'awaiting-2fa')
  useEffect(() => {
    if (awaitingAccount && twoFaTarget !== awaitingAccount.id) {
      setTwoFaTarget(awaitingAccount.id)
    }
  }, [awaitingAccount, twoFaTarget])

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (meData.user.role !== 'admin') return <Navigate to="/" />

  return (
    <SettingsLayout
      title="Belegabruf"
      description="Automatischer Download von Rechnungen und Gutschriften externer Anbieter."
    >
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): ＋ Konto hinzufügen in der Bottom-Bar —
          derselbe Handler wie der Kopf-Button. */}
      <MobileBottomSlots>
        <BarAccentPlus ariaLabel="Konto hinzufügen" onClick={() => setAddOpen(true)} />
      </MobileBottomSlots>

      <div className="space-y-6">
        {/* Actions — <640 übernimmt die Bottom-Bar (phone:hidden). */}
        <div className="flex phone:hidden justify-end">
          <Button variant="primary" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" />
            Konto hinzufügen
          </Button>
        </div>

        {/* Account list */}
        {accounts.length === 0 ? (
          <div className="card-elevated px-6 py-16 text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent">
              <Download className="h-7 w-7" strokeWidth={1.5} />
            </div>
            <p className="mt-4 text-base font-medium text-text-primary">
              Keine Konten eingerichtet
            </p>
            <p className="mt-1 text-sm text-text-tertiary max-w-sm mx-auto">
              Füge ein Anbieterkonto hinzu, um automatisch Rechnungen und Gutschriften abzurufen.
            </p>
            <Button className="mt-5" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" /> Konto hinzufügen
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {accounts.map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                providers={providers}
                onSync={() => syncMutation.mutate(account.id)}
                onEdit={() => setEditTarget(account)}
                onDelete={() => {
                  void confirm({
                    title: `„${account.label}“ löschen?`,
                    description:
                      'Das Konto und alle abgerufenen Belege werden unwiderruflich gelöscht.',
                    variant: 'danger',
                    confirmLabel: 'Löschen',
                  }).then((ok) => {
                    if (ok) deleteMutation.mutate(account.id)
                  })
                }}
                syncing={syncMutation.isPending}
              />
            ))}
          </div>
        )}

        {/* 2FA prompt */}
        {twoFaTarget && (
          <TwoFaDialog
            accountId={twoFaTarget}
            onClose={() => setTwoFaTarget(null)}
            onSubmitted={() => {
              setTwoFaTarget(null)
              qc.invalidateQueries({ queryKey: ['fetcher'] })
            }}
          />
        )}

        {/* Add account panel */}
        {addOpen && (
          <AddAccountPanel
            providers={providers}
            onClose={() => setAddOpen(false)}
            onCreated={() => {
              setAddOpen(false)
              qc.invalidateQueries({ queryKey: ['fetcher'] })
            }}
          />
        )}

        {/* Edit account panel */}
        {editTarget && (
          <EditAccountPanel
            account={editTarget}
            onClose={() => setEditTarget(null)}
            onSaved={() => {
              setEditTarget(null)
              qc.invalidateQueries({ queryKey: ['fetcher'] })
            }}
          />
        )}
      </div>
    </SettingsLayout>
  )
}

// ============================================================================
// Account Card
// ============================================================================

function AccountCard({
  account,
  providers,
  onSync,
  onEdit,
  onDelete,
  syncing,
}: {
  account: FetcherAccount
  providers: FetcherProviderType[]
  onSync: () => void
  onEdit: () => void
  onDelete: () => void
  syncing: boolean
}) {
  const provider = providers.find((p) => p.id === account.provider)
  const providerName = provider?.name ?? account.provider

  return (
    <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-5">
      <div className="flex items-start gap-4">
        {/* Provider icon area */}
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
          <ProviderIcon provider={account.provider} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-text-primary truncate">{account.label}</h3>
            <StatusBadge status={account.status} />
            {account.hasTotp && (
              <span className="inline-flex items-center gap-1 text-2xs uppercase tracking-wide text-success bg-success/10 px-1.5 py-0.5 rounded-md font-medium">
                <ShieldCheck className="h-3 w-3" /> TOTP
              </span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-text-secondary">{providerName}</p>
          <p className="text-xs text-text-tertiary">{account.email}</p>

          {/* Last sync info */}
          <div className="mt-2 flex items-center gap-4 text-xs text-text-tertiary">
            {account.lastSyncAt && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Letzte Sync: {new Date(account.lastSyncAt).toLocaleString('de-DE')}
              </span>
            )}
            {account.lastSyncCount !== null && account.lastSyncCount > 0 && (
              <span>{account.lastSyncCount} neue Belege</span>
            )}
          </div>

          {/* Error display */}
          {account.lastSyncError && (
            <div className="mt-2 rounded-lg bg-danger/5 border border-danger/20 px-3 py-2 text-xs text-danger">
              <AlertTriangle className="inline h-3 w-3 mr-1" />
              {account.lastSyncError}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onSync}
            disabled={syncing || account.status === 'syncing'}
            title="Sync starten"
          >
            {account.status === 'syncing' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Sync
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit} title="Bearbeiten">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} title="Löschen">
            <Trash2 className="h-4 w-4 text-danger" />
          </Button>
        </div>
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    active: { label: 'Aktiv', cls: 'bg-success/15 text-success border-success/20' },
    syncing: { label: 'Synchronisiert…', cls: 'bg-accent/15 text-accent border-accent/20' },
    'awaiting-2fa': {
      label: '2FA erforderlich',
      cls: 'bg-warning/15 text-warning border-warning/20',
    },
    error: { label: 'Fehler', cls: 'bg-danger/15 text-danger border-danger/20' },
  }
  const m = map[status] ?? map.active
  return (
    <span
      className={`px-2 py-0.5 text-2xs uppercase tracking-wide rounded-md border font-medium ${m.cls}`}
    >
      {m.label}
    </span>
  )
}

function ProviderIcon({ provider }: { provider: string }) {
  // Simple text-based icon for now
  if (provider === 'amazon-business') {
    return <span className="text-lg font-bold">A</span>
  }
  return <Download className="h-5 w-5" />
}

// ============================================================================
// Add Account Panel
// ============================================================================

function AddAccountPanel({
  providers,
  onClose,
  onCreated,
}: {
  providers: FetcherProviderType[]
  onClose: () => void
  onCreated: () => void
}) {
  const [provider, setProvider] = useState(providers[0]?.id ?? '')
  const [label, setLabel] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpSecret, setTotpSecret] = useState('')

  const createMutation = useMutation({
    mutationFn: () =>
      api.createFetcherAccount({
        provider,
        label,
        email,
        password,
        totpSecret: totpSecret.trim() || null,
      }),
    onSuccess: () => {
      notify.success('Konto erstellt')
      onCreated()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const canSubmit = provider && label.trim() && email.trim() && password.trim()

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Abrufkonto hinzufügen"
      description="Zugangsdaten für den automatischen Belegabruf."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="fa-provider">Anbieter</Label>
          <Select id="fa-provider" value={provider} onChange={(e) => setProvider(e.target.value)}>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="fa-label">Bezeichnung</Label>
          <Input
            id="fa-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="z.B. IT Financeflow Amazon"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fa-email">E-Mail</Label>
          <Input
            id="fa-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="login@firma.de"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fa-password">Passwort</Label>
          <Input
            id="fa-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fa-totp">TOTP-Secret (optional)</Label>
          <div className="flex gap-2">
            <Input
              id="fa-totp"
              type="password"
              value={totpSecret}
              onChange={(e) => setTotpSecret(e.target.value)}
              placeholder="Base32-Secret"
            />
            <label className="shrink-0">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  try {
                    const secret = await extractTotpFromQrImage(file)
                    setTotpSecret(secret)
                    notify.success('TOTP-Secret aus QR-Code extrahiert')
                  } catch (err) {
                    notify.error((err as Error).message)
                  }
                  e.target.value = ''
                }}
              />
              <span className="inline-flex items-center justify-center h-9 px-3 rounded-lg border border-border-default bg-bg-base text-text-secondary hover:text-text-primary cursor-pointer text-sm touch:opacity-100">
                <QrCode className="h-4 w-4 mr-1.5" />
                QR scannen
              </span>
            </label>
          </div>
          <p className="text-xs text-text-tertiary">
            TOTP-Secret oder QR-Code deiner Authenticator-App. Wird verschlüsselt gespeichert.
          </p>
        </div>
      </div>
    </SidePanel>
  )
}

// ============================================================================
// Edit Account Panel
// ============================================================================

function EditAccountPanel({
  account,
  onClose,
  onSaved,
}: {
  account: FetcherAccount
  onClose: () => void
  onSaved: () => void
}) {
  const [label, setLabel] = useState(account.label)
  const [email, setEmail] = useState(account.email)
  const [password, setPassword] = useState('')
  const [totpSecret, setTotpSecret] = useState('')

  const updateMutation = useMutation({
    mutationFn: () => {
      const data: Record<string, unknown> = {}
      if (label.trim() !== account.label) data.label = label.trim()
      if (email.trim() !== account.email) data.email = email.trim()
      if (password.trim()) data.password = password.trim()
      if (totpSecret.trim()) data.totpSecret = totpSecret.trim()
      return api.updateFetcherAccount(account.id, data)
    },
    onSuccess: () => {
      notify.success('Gespeichert')
      onSaved()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Konto bearbeiten"
      description={account.label}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="fe-label">Bezeichnung</Label>
          <Input id="fe-label" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fe-email">E-Mail</Label>
          <Input
            id="fe-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fe-password">Neues Passwort (leer lassen = unverändert)</Label>
          <Input
            id="fe-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fe-totp">Neues TOTP-Secret (leer = unverändert)</Label>
          <div className="flex gap-2">
            <Input
              id="fe-totp"
              type="password"
              value={totpSecret}
              onChange={(e) => setTotpSecret(e.target.value)}
            />
            <label className="shrink-0">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  try {
                    const secret = await extractTotpFromQrImage(file)
                    setTotpSecret(secret)
                    notify.success('TOTP-Secret aus QR-Code extrahiert')
                  } catch (err) {
                    notify.error((err as Error).message)
                  }
                  e.target.value = ''
                }}
              />
              <span className="inline-flex items-center justify-center h-9 px-3 rounded-lg border border-border-default bg-bg-base text-text-secondary hover:text-text-primary cursor-pointer text-sm touch:opacity-100">
                <QrCode className="h-4 w-4 mr-1.5" />
                QR scannen
              </span>
            </label>
          </div>
        </div>
      </div>
    </SidePanel>
  )
}

// ============================================================================
// 2FA Dialog
// ============================================================================

function TwoFaDialog({
  accountId,
  onClose,
  onSubmitted,
}: {
  accountId: string
  onClose: () => void
  onSubmitted: () => void
}) {
  const [code, setCode] = useState('')

  const submitMutation = useMutation({
    mutationFn: () => api.submitFetcher2fa(accountId, code),
    onSuccess: () => {
      notify.success('2FA-Code übermittelt')
      onSubmitted()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title="2FA-Code eingeben"
      description="Der Sync wartet auf einen Bestätigungscode."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            disabled={!code.trim() || submitMutation.isPending}
            onClick={() => submitMutation.mutate()}
          >
            <KeyRound className="h-4 w-4" />
            Code senden
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        <div className="rounded-lg bg-warning/5 border border-warning/20 px-4 py-3 text-sm text-warning">
          <AlertTriangle className="inline h-4 w-4 mr-2" />
          Der Anbieter verlangt einen 2FA-Bestätigungscode. Gib den Code aus deiner
          Authenticator-App ein.
        </div>
        <div className="space-y-2">
          <Label htmlFor="tfa-code">Code</Label>
          <Input
            id="tfa-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            autoFocus
            maxLength={10}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && code.trim()) submitMutation.mutate()
            }}
          />
        </div>
      </div>
    </SidePanel>
  )
}
